import type {
  TranslationBatchRetryAttempt,
  TranslationBatchResumeState,
  WorkbenchPropagationFailure,
} from '../api/workbenchPropagationAPI'

export type TranslationPreviewResumeSurface = 'workbench' | 'ledger-safe'

export const AUTOMATIC_TRANSLATION_BATCH_RETRY_LIMIT = 2

const ANCHOR_LOCALIZATION_FAILURE_PATTERN = /could not locate copied translation span|translation span order regressed/i
const RETRYABLE_TRANSLATION_BATCH_FAILURE_PATTERN = /did not include assignments|malformed json|failed to extract|invalid json|unexpected end of json/i

export interface LedgerSafeSelectedRepairResumeMetadata {
  selectedRowIds: string[]
  requestRowIds: string[]
  repairCandidateRowIds?: string[]
  contextRowIds: string[]
  anchorRowIds: string[]
  repairTargetSpan: {
    startChar: number
    endChar: number
    text: string
  }
  modelId: string
  thinkingLevel: string
}

export interface TranslationPreviewResumeRecord {
  id: string
  surface: TranslationPreviewResumeSurface
  contextId: string
  witnessId: string
  scopeId: string | null
  rowSignature: string
  witnessTextHash: string
  modelId: string | null
  batchSize: number | null
  resumeState: TranslationBatchResumeState
  pendingResumeState?: TranslationBatchResumeState | null
  selectedRepair?: LedgerSafeSelectedRepairResumeMetadata
  failure: WorkbenchPropagationFailure | null
  updatedAt: number
}

const hashString = (value: string): string => {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return `h${(hash >>> 0).toString(16)}`
}

export const buildTranslationPreviewRowSignature = (rowIds: string[]): string => (
  hashString(rowIds.join('\u241f'))
)

export const buildTranslationPreviewTextHash = (text: string): string => hashString(text)

export const appendTranslationBatchRetryAttempt = (
  history: TranslationBatchRetryAttempt[] | undefined,
  attempt: TranslationBatchRetryAttempt,
): TranslationBatchRetryAttempt[] => ([...(history || []), attempt].slice(-24))

export const summarizeTranslationBatchRetryHistory = (
  history: TranslationBatchRetryAttempt[] | undefined,
): string | undefined => {
  const attempts = history || []
  if (attempts.length === 0) {
    return undefined
  }
  const automaticAttempts = attempts.filter(attempt => attempt.automatic)
  if (automaticAttempts.length === 0) {
    return undefined
  }
  const distinctBatches = new Set(automaticAttempts.map(attempt => attempt.batchIndex)).size
  return `${automaticAttempts.length} automatic retr${automaticAttempts.length === 1 ? 'y' : 'ies'} recorded across ${distinctBatches} batch${distinctBatches === 1 ? '' : 'es'}.`
}

const normalizeContiguousRepairCandidateRowIds = (
  candidateRowIds: unknown,
  requestRowIds: string[],
): string[] => {
  if (!Array.isArray(candidateRowIds) || candidateRowIds.length === 0) {
    return []
  }
  const normalized = candidateRowIds.filter(
    (rowId): rowId is string => typeof rowId === 'string' && Boolean(rowId.trim())
  )
  if (normalized.length !== candidateRowIds.length || new Set(normalized).size !== normalized.length) {
    return []
  }
  const requestPositionById = new Map(requestRowIds.map((rowId, index) => [rowId, index]))
  const positions = normalized.map(rowId => requestPositionById.get(rowId))
  if (positions.some(position => typeof position !== 'number')) {
    return []
  }
  const firstPosition = positions[0] as number
  if (positions.some((position, index) => position !== firstPosition + index)) {
    return []
  }
  return normalized
}

export const recoverLedgerSafeRepairCandidateRowIds = ({
  candidateRowIds,
  requestRowIds,
  resumeState,
}: {
  candidateRowIds?: string[]
  requestRowIds: string[]
  resumeState: TranslationBatchResumeState
}): string[] => {
  const persistedCandidateRowIds = normalizeContiguousRepairCandidateRowIds(
    candidateRowIds,
    requestRowIds,
  )
  if (persistedCandidateRowIds.length > 0) {
    return persistedCandidateRowIds
  }

  for (const diagnostics of resumeState.batchDiagnostics) {
    const diagnosticCandidateRowIds = normalizeContiguousRepairCandidateRowIds(
      diagnostics.generationConfig.repairCandidateRowIds,
      requestRowIds,
    )
    if (diagnosticCandidateRowIds.length > 0) {
      return diagnosticCandidateRowIds
    }

    const candidateRange = diagnostics.generationConfig.repairCandidateRange
    if (!candidateRange || typeof candidateRange !== 'object') {
      continue
    }
    const startRowId = (candidateRange as Record<string, unknown>).startRowId
    const endRowId = (candidateRange as Record<string, unknown>).endRowId
    if (typeof startRowId !== 'string' || typeof endRowId !== 'string') {
      continue
    }
    const startIndex = requestRowIds.indexOf(startRowId)
    const endIndex = requestRowIds.indexOf(endRowId)
    if (startIndex >= 0 && endIndex >= startIndex) {
      return requestRowIds.slice(startIndex, endIndex + 1)
    }
  }

  return []
}

export const shouldAutoRetryTranslationBatchFailure = ({
  error,
  diagnostics,
  partial = false,
}: {
  error: string
  diagnostics?: WorkbenchPropagationFailure['diagnostics']
  partial?: boolean
}): boolean => {
  if (ANCHOR_LOCALIZATION_FAILURE_PATTERN.test(error)) {
    return false
  }
  if (partial) {
    return true
  }
  if (diagnostics?.parseStatus === 'failed' || Boolean(diagnostics?.parseError)) {
    return true
  }
  return RETRYABLE_TRANSLATION_BATCH_FAILURE_PATTERN.test(error)
}

export const buildWorkbenchTranslationPreviewResumeId = (contextId: string, witnessId: string): string => (
  `workbench:${contextId}:${witnessId}`
)

export const buildLedgerSafeTranslationPreviewResumeId = (
  contextId: string,
  witnessId: string,
  scopeId: string | null,
): string => (
  `ledger-safe:${contextId}:${scopeId || 'aggregate'}:${witnessId}`
)

export const buildLedgerSafeSelectedRepairResumeScopeId = ({
  baseScopeId,
  selectedRowIds,
  startChar,
  endChar,
  spanText,
  modelId,
  thinkingLevel,
}: {
  baseScopeId: string | null
  selectedRowIds: string[]
  startChar: number
  endChar: number
  spanText: string
  modelId: string
  thinkingLevel: string
}): string => {
  const operationSignature = hashString([
    selectedRowIds.join('\u241f'),
    String(startChar),
    String(endChar),
    hashString(spanText),
    modelId,
    thinkingLevel,
  ].join('\u241e'))
  return `${baseScopeId || 'aggregate'}:selected-repair:${operationSignature}`
}

export const buildTranslationPreviewResumeRecord = ({
  id,
  surface,
  contextId,
  witnessId,
  scopeId = null,
  rowIds,
  witnessText,
  resumeState,
  pendingResumeState = null,
  selectedRepair,
  failure = null,
  modelId = null,
  batchSize = null,
}: {
  id: string
  surface: TranslationPreviewResumeSurface
  contextId: string
  witnessId: string
  scopeId?: string | null
  rowIds: string[]
  witnessText: string
  resumeState: TranslationBatchResumeState
  pendingResumeState?: TranslationBatchResumeState | null
  selectedRepair?: LedgerSafeSelectedRepairResumeMetadata
  failure?: WorkbenchPropagationFailure | null
  modelId?: string | null
  batchSize?: number | null
}): TranslationPreviewResumeRecord => ({
  id,
  surface,
  contextId,
  witnessId,
  scopeId,
  rowSignature: buildTranslationPreviewRowSignature(rowIds),
  witnessTextHash: buildTranslationPreviewTextHash(witnessText),
  modelId,
  batchSize,
  resumeState,
  ...(pendingResumeState ? { pendingResumeState } : {}),
  ...(selectedRepair ? { selectedRepair } : {}),
  failure,
  updatedAt: Date.now(),
})

export const isTranslationPreviewResumeRecordCurrent = ({
  record,
  contextId,
  witnessId,
  scopeId = null,
  rowIds,
  witnessText,
}: {
  record: TranslationPreviewResumeRecord
  contextId: string
  witnessId: string
  scopeId?: string | null
  rowIds: string[]
  witnessText: string
}): boolean => (
  record.contextId === contextId
  && record.witnessId === witnessId
  && (record.scopeId || null) === scopeId
  && record.rowSignature === buildTranslationPreviewRowSignature(rowIds)
  && record.witnessTextHash === buildTranslationPreviewTextHash(witnessText)
)
